'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { usePathname, useRouter } from 'next/navigation';
import type { TourDemoHandlers, TourStep } from './types';

interface TourContextValue {
  active: boolean;
  index: number;
  step: TourStep | null;
  steps: TourStep[];
  start: (steps: TourStep[]) => void;
  next: () => void;
  back: () => void;
  skip: () => void;
  registerDemoHandlers: (handlers: TourDemoHandlers) => () => void;
}

const TourContext = createContext<TourContextValue | null>(null);

export function useTour(): TourContextValue {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTour must be used within <TourProvider>');
  return ctx;
}

/** Optional accessor — returns null outside a TourProvider. Used by code paths
 *  that should no-op when the tour system isn't mounted. */
export function useOptionalTour(): TourContextValue | null {
  return useContext(TourContext);
}

export function TourProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const [active, setActive] = useState(false);
  const [index, setIndex] = useState(0);
  const [steps, setSteps] = useState<TourStep[]>([]);
  // Held in a ref so step.onEnter/onExit always see the latest demo handlers
  // registered by whichever page is currently mounted.
  const demoHandlersRef = useRef<TourDemoHandlers>({});

  const registerDemoHandlers = useCallback((handlers: TourDemoHandlers) => {
    demoHandlersRef.current = { ...demoHandlersRef.current, ...handlers };
    return () => {
      // Best-effort cleanup: clear only the keys this caller set.
      for (const key of Object.keys(handlers) as Array<keyof TourDemoHandlers>) {
        if (demoHandlersRef.current[key] === handlers[key]) {
          delete demoHandlersRef.current[key];
        }
      }
    };
  }, []);

  const runStep = useCallback(
    (nextIndex: number, prevIndex: number | null) => {
      const prev = prevIndex != null ? steps[prevIndex] : null;
      const next = steps[nextIndex];
      if (prev) {
        try {
          prev.onExit?.({ demoHandlers: demoHandlersRef.current });
        } catch (err) {
          console.error('[tour] onExit failed', err);
        }
      }
      if (!next) return;
      // Cross-route step: navigate first; the spotlight will wait for the
      // target element to mount via the observer in TourOverlay.
      if (next.route && next.route !== pathname) {
        router.push(next.route);
      }
      if (next.onEnter) {
        // Defer slightly so any navigation in this tick has a chance to start
        // and any previous overlay's close animation can complete.
        const timer = window.setTimeout(() => {
          try {
            next.onEnter?.({ demoHandlers: demoHandlersRef.current });
          } catch (err) {
            console.error('[tour] onEnter failed', err);
          }
        }, 300);
        return () => window.clearTimeout(timer);
      }
      return undefined;
    },
    [steps, router, pathname],
  );

  const start = useCallback(
    (nextSteps: TourStep[]) => {
      if (nextSteps.length === 0) return;
      setSteps(nextSteps);
      setIndex(0);
      setActive(true);
      // Run onEnter for the first step on the next tick once state has settled.
      window.setTimeout(() => {
        const first = nextSteps[0];
        if (first?.route && first.route !== pathname) {
          router.push(first.route);
        }
        try {
          first?.onEnter?.({ demoHandlers: demoHandlersRef.current });
        } catch (err) {
          console.error('[tour] onEnter failed', err);
        }
      }, 0);
    },
    [router, pathname],
  );

  // runStep does side effects (router.push, onEnter/onExit). Never call it
  // inside a setIndex updater — React flags any setState during another
  // component's render. Read `index` from the closure and run side effects
  // before bumping state.
  const next = useCallback(() => {
    const target = index + 1;
    if (target >= steps.length) {
      try {
        steps[index]?.onExit?.({ demoHandlers: demoHandlersRef.current });
      } catch (err) {
        console.error('[tour] onExit failed', err);
      }
      setActive(false);
      return;
    }
    runStep(target, index);
    setIndex(target);
  }, [index, steps, runStep]);

  const back = useCallback(() => {
    const target = index - 1;
    if (target < 0) return;
    runStep(target, index);
    setIndex(target);
  }, [index, runStep]);

  const skip = useCallback(() => {
    try {
      steps[index]?.onExit?.({ demoHandlers: demoHandlersRef.current });
    } catch (err) {
      console.error('[tour] onExit failed', err);
    }
    setActive(false);
  }, [steps, index]);

  // ESC key closes the tour
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') skip();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [active, skip]);

  const value = useMemo<TourContextValue>(
    () => ({
      active,
      index,
      step: active ? (steps[index] ?? null) : null,
      steps,
      start,
      next,
      back,
      skip,
      registerDemoHandlers,
    }),
    [active, index, steps, start, next, back, skip, registerDemoHandlers],
  );

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}
